import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

import { Environment } from '../../constants/environments';
import { HttpVerb } from '../../constants/httpVerbs';
import { Event } from '../../types/event';
import { logger } from '../../utils/logger';
import { useApiService } from '../useApiService';
import { useLoaderScreenContext } from './loader-screen.context';

/**
 * Simplified event type for resale pages
 * Contains only the fields needed by ShareResaleEvent and ResaleAppBar
 */
export type SimpleEvent = {
  id: number;
  title: string;
  slug: string;
  poster?: {
    small: string;
    medium: string;
    large: string;
    xLarge: string;
  };
  place?: {
    id: number;
    name: string;
    city: string;
    state: string;
    street: string;
    zip: string;
    country: string;
    location: {
      lat: number;
      lon: number;
    };
  };
};

export type ResaleEventContextProps = {
  event: SimpleEvent | null;
  loading: boolean;
  hasError: boolean;
  fetchEventById: (eventId: number) => Promise<boolean>;
};

const ResaleEventContext = createContext({} as ResaleEventContextProps);

function ResaleEventProvider({ children }: { children: ReactNode }) {
  const [event, setEvent] = useState<SimpleEvent | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { request } = useApiService();
  const { toggleLoader } = useLoaderScreenContext();

  /**
   * Fetches complete event details by ID from the event API endpoint.
   * This function retrieves full event information including poster images,
   * which are not available in the resale API response.
   *
   * Endpoint: GET /public/{eventId}
   * Example: https://sbx-event.ingresse.com/public/89991
   *
   * @param eventId - The unique identifier for the event
   * @returns Promise<boolean> - True if event data was fetched successfully, false otherwise
   */
  const fetchEventById = useCallback(async (eventId: number): Promise<boolean> => {
    if (!eventId) {
      logger.warn('Cannot fetch event: eventId is required');
      return false;
    }

    try {
      toggleLoader({ show : true });
      setLoading(true);
      setHasError(false);

      const resourcePath = `/public/${eventId}`;

      type ResponseError = {
        status: boolean;
        category: string;
        code: number;
        message: string;
      };

      const response = await request<
        null,
        { data: Event; responseStatus: number; responseError?: ResponseError }
      >(
        {
          requestOptions : { resourcePath },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_EVENT_URL }
      );

      // Check if response contains an error (HTTP status or error object)
      if (response?.responseError || (response?.responseStatus && response.responseStatus >= 400)) {
        const errorMessage = response?.responseError?.message || `HTTP ${response?.responseStatus}`;
        logger.error(`Event ${eventId} returned error: ${errorMessage}`);
        setEvent(null);
        setHasError(true);
        return false;
      }

      const eventData = response?.data;

      if (!eventData) {
        logger.warn(`Event ${eventId} not found`);
        setEvent(null);
        return false;
      }

      setEvent(eventData);
      return true;
    } catch (error) {
      logger.error(`Failed to fetch event ${eventId}: ${error}`);
      setEvent(null);
      setHasError(true);
      return false;
    } finally {
      setLoading(false);
      toggleLoader({ show : false });
    }
  }, [request, toggleLoader]);

  const shared = {
    event,
    loading,
    hasError,
    fetchEventById,
  };

  return (
    <ResaleEventContext.Provider value={shared}>
      {children}
    </ResaleEventContext.Provider>
  );
}

ResaleEventProvider.displayName = 'ResaleEventProvider';

export const useResaleEventContext = (): ResaleEventContextProps =>
  useContext(ResaleEventContext);

export { ResaleEventContext, ResaleEventProvider };
